import React, { HTMLInputTypeAttribute, ReactNode } from 'react'
import clsx from 'clsx'
import { SizeVariants } from '@/shared/forms/components/constants'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons'
import OptionalLabel from '@/shared/forms/components/OptionalLabel'
import { SimpleTooltip } from '@/shared/components/common/Tooltip'

export type FormikTextInputVariant = 'text' | 'multiline'
export const FormikTextInputVariant = {
  Text: 'text' as const,
  Multiline: 'multiline' as const,
}

export type BaseTextInputProps = {
  value?: string
  size: keyof typeof SizeVariants
  touched?: boolean
  error?: string
  autocomplete?: string
  variant?: FormikTextInputVariant
  disabled?: boolean
  placeholder?: string
  optional?: boolean
  type?: HTMLInputTypeAttribute
  rows?: number
  resizable?: boolean
  editable?: boolean
  hideLabel?: boolean
  inputMode?:
    | 'none'
    | 'text'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search'

  // Sometimes we just want to show the red ring around the input without the error message attached
  hasError?: boolean
  verified?: boolean
  showErrorText?: boolean
  inputClassname?: string
  tooltip?: ReactNode
  verificationAppearance?: 'badge' | 'circle'
  appearance?: 'default' | 'portal-profile'
}

export type Props = {
  id?: string
  label: string
  name: string
  onChange?: (e: React.ChangeEvent<any>) => void
  onBlur?: (e: React.FocusEvent<any>) => void
} & BaseTextInputProps

export default function TextInput({
  id,
  name,
  label,
  size,
  autocomplete,
  placeholder,
  value,
  onChange,
  onBlur,
  disabled = false,
  touched = false,
  error = '',
  rows = 6,
  resizable = true,
  optional = false,
  type = 'text',
  variant = FormikTextInputVariant.Text,
  editable = true,
  hideLabel = false,
  inputMode = 'text',
  hasError,
  verified,
  showErrorText = true,
  inputClassname = '',
  tooltip,
  verificationAppearance = 'badge',
  appearance = 'default',
  ...props
}: Props) {
  const inputId = id ?? name

  return (
    <div className={SizeVariants[size as keyof typeof SizeVariants]}>
      <div className="flex items-end justify-between">
        <div className="flex items-center gap-x-2">
          <label
            htmlFor={inputId}
            className={clsx(
              'block text-sm font-medium leading-6',
              disabled ? 'text-gray-500' : 'text-gray-900',
              hideLabel && 'sr-only',
            )}
          >
            {label}
          </label>
          {tooltip && <SimpleTooltip content={tooltip} />}
        </div>
        <OptionalLabel optional={optional} />
      </div>
      <div className="relative mt-2">
        {variant === FormikTextInputVariant.Text ? (
          <>
            <input
              type={type}
              className={clsx(
                appearance === 'portal-profile'
                  ? 'block h-[52px] w-full rounded-[14px] border-[1.5px] bg-[#F4F4F6] px-4 py-0 text-[15px] leading-5 text-[#0B0B0F] outline-none transition-colors duration-300 placeholder:text-[#A0A0A8] focus:border-[#0B0B0F]/45'
                  : 'block h-10 w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6',
                (touched && error) || hasError
                  ? appearance === 'portal-profile'
                    ? 'border-[#C6453C]'
                    : 'ring-red-600'
                  : appearance === 'portal-profile'
                    ? 'border-transparent'
                    : 'ring-gray-300',
                disabled
                  ? appearance === 'portal-profile'
                    ? 'cursor-not-allowed opacity-60'
                    : 'bg-slate-300'
                  : '',
                inputClassname,
              )}
              disabled={disabled}
              id={inputId}
              value={value}
              name={name}
              onChange={onChange}
              onBlur={onBlur}
              placeholder={placeholder}
              inputMode={inputMode}
              {...(autocomplete ? { autoComplete: autocomplete } : {})}
              {...props}
            />
            {verified && (
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                {verificationAppearance === 'circle' ? (
                  <svg
                    aria-hidden="true"
                    className="h-[15px] w-[15px] text-[#1F8A70]"
                    fill="none"
                    viewBox="0 0 16 16"
                  >
                    <circle
                      cx="8"
                      cy="8"
                      r="6.25"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path
                      d="m5.25 8.1 1.75 1.75 3.75-4"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                    />
                  </svg>
                ) : (
                  <FontAwesomeIcon
                    icon={faBadgeCheck}
                    className="h-5 w-5 text-green-500"
                    aria-hidden="true"
                  />
                )}
              </div>
            )}
          </>
        ) : (
          <textarea
            className={clsx(
              'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-inset placeholder:text-gray-400 sm:text-sm sm:leading-6',
              (touched && error) || hasError ? 'ring-red-600' : 'ring-gray-300',
              disabled ? 'bg-slate-300' : '',
              editable
                ? 'ring-1 focus:ring-2 focus:ring-inset focus:ring-blue-600'
                : 'bg-slate-100 ring-0 focus:ring-0',
              resizable ? '' : 'resize-none',
              inputClassname,
            )}
            rows={rows}
            disabled={disabled}
            id={inputId}
            value={value}
            name={name}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder}
            readOnly={!editable}
            inputMode={inputMode}
            {...(autocomplete ? { autoComplete: autocomplete } : {})}
            {...props}
          />
        )}
        {touched && error && showErrorText && (
          <div className="mt-2 text-sm font-semibold text-red-600">{error}</div>
        )}
      </div>
    </div>
  )
}
